import { faLockKeyhole } from '@fortawesome/sharp-solid-svg-icons';
import { CompanyType, OfferType } from 'src/types';

import Head from 'next/head';
import { cn } from 'src/utils/tailwind';
import { Icon } from '../ui/Icon';
import { Lia } from '../ui/Lia';
import { Text } from '../ui/Text';
import { Title } from '../ui/Title';

export const enum CheckoutHeaderVariants {
  COMPACT = 'compact',
  FINANCING = 'financing',
  CHECKOUT = 'checkout',
}

type CheckoutHeaderProps =
  | {
      company: CompanyType;
      offer: OfferType;
      variant: CheckoutHeaderVariants.CHECKOUT;
      showBanner?: boolean;
      isDisabled?: boolean;
    }
  | {
      company: CompanyType;
      variant: CheckoutHeaderVariants.COMPACT;
      offer?: OfferType;
      showBanner?: boolean;
      isDisabled?: boolean;
    }
  | {
      company: CompanyType;
      offer: OfferType;
      variant: CheckoutHeaderVariants.FINANCING;
      showBanner?: boolean;
      isDisabled?: boolean;
    };

const CheckoutHeader = (props: CheckoutHeaderProps) => {
  const { company, offer, isDisabled } = props;

  return (
    <header
      className={cn('p-5 sm:px-0 sm:py-10', {
        'bg-background-least': !props.showBanner || !offer.product.bannerUrl || isDisabled,
        'pb-0 sm:pb-0': props.showBanner && offer.product.bannerUrl,
      })}
    >
      <div className="mx-auto max-w-xl">
        <div className="flex items-center justify-between gap-3">
          {Boolean(company.logo) && (
            <img
              alt={company.name}
              src={company.logo}
              width="100"
              height="52"
              className="h-14 max-w-[250px] object-contain mix-blend-darken"
            />
          )}
          {!company.logo && (
            <Title
              as="h1"
              size="lg"
            >
              {company.name}
            </Title>
          )}

          <div className="flex items-center gap-2">
            <Lia />
            <span className="block h-[22px] w-px bg-coal-300" />
            <Icon
              icon={faLockKeyhole}
              className="text-foreground-subtle"
            />
            <span className="uppercase">
              <Text
                className="block text-xs text-foreground-subtle"
                weight="bold"
              >
                100%
              </Text>
              <Text
                className="text-xs text-foreground-least"
                weight="bold"
              >
                Seguro
              </Text>
            </span>
          </div>
        </div>

        {props.variant === CheckoutHeaderVariants.FINANCING && (
          <>
            <Title
              className="mb-5 mt-10"
              size="md"
            >
              Fluxo de parcelamento
            </Title>

            <Text
              size="sm"
              weight="medium"
              className="text-foreground-subtle"
            >
              {offer.product.kind.name}
            </Text>
            <Text
              size="md"
              weight="bold"
              className="text-foreground-subtle"
            >
              {offer.product.name}
            </Text>
          </>
        )}

        {props.variant === CheckoutHeaderVariants.CHECKOUT && (
          <>
            {props.showBanner && offer.product.bannerUrl && !isDisabled && (
              <>
                <Head>
                  <link
                    rel="preload"
                    as="image"
                    href={offer.product.bannerUrl}
                    fetchpriority="high"
                  />
                </Head>
                <div className="my-10 flex h-[205px] max-w-full items-center overflow-hidden rounded-lg object-cover sm:h-[302px]">
                  <img
                    src={offer.product.bannerUrl}
                    alt={offer.product.name}
                    className="max-w-full overflow-hidden rounded-lg"
                    width="576"
                    height="302"
                  />
                </div>
              </>
            )}

            <Title
              size="xs"
              className={cn('mt-10', {
                'text-foreground-subtle': !isDisabled,
                'text-foreground-least': isDisabled,
              })}
              data-testid="product-kind"
            >
              {offer.product.kind.name}
              {offer.name ? ` - ${offer.name}` : ''}
            </Title>
            <Title
              size="lg"
              as="h1"
              className={cn({
                'text-accent-main': !isDisabled,
                'text-foreground-least': isDisabled,
              })}
            >
              {offer.product.name}
            </Title>

            {offer.product.description && (
              <Text
                size="sm"
                className={cn('mb-4', {
                  'text-foreground-subtle': !isDisabled,
                  'text-foreground-least': isDisabled,
                })}
              >
                {offer.product.description}
              </Text>
            )}
          </>
        )}
      </div>
    </header>
  );
};

export { CheckoutHeader };
